/**
 * Portfolio + Services + Bookings tabs shown to an approved stylist in
 * their Studio dashboard. Kept as a single file to avoid explosion of
 * small components — each tab is a self-contained function.
 */
import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Plus, Loader2, Trash2, Eye, EyeOff, ExternalLink, Calendar, Clock,
  Check, X, Video, Home as HomeIcon, Store, Scissors, Sparkles, User,
  IndianRupee, Image as ImageIcon, FileText, Send,
} from "lucide-react";

const SERVICE_KIND_META = {
  video: { icon: Video, label: "Video consultation" },
  home_visit: { icon: HomeIcon, label: "Home visit" },
  studio_visit: { icon: Store, label: "Studio visit" },
  hair: { icon: Scissors, label: "Hair" },
  makeup: { icon: Sparkles, label: "Makeup" },
  styling: { icon: User, label: "Personal styling" },
  consult: { icon: User, label: "General consult" },
};

export function PortfolioTab() {
  const [looks, setLooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", price_inr: "", tags: "", buy_url: "", published: true });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/stylists/me/looks")
      .then(({ data }) => setLooks(data.items || []))
      .catch(() => setLooks([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Add a photo first."); return; }
    if (!form.title.trim()) { toast.error("Add a title."); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      fd.append("title", form.title);
      if (form.description) fd.append("description", form.description);
      if (form.price_inr) fd.append("price_inr", form.price_inr);
      if (form.tags) fd.append("tags", form.tags);
      if (form.buy_url) fd.append("buy_url", form.buy_url);
      fd.append("published", form.published ? "true" : "false");
      await api.post("/stylists/me/looks", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Look published.");
      setForm({ title: "", description: "", price_inr: "", tags: "", buy_url: "", published: true });
      setFile(null); setPreview(null); setShowForm(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed.");
    } finally { setSaving(false); }
  };

  const toggle = async (l) => {
    try {
      await api.patch(`/stylists/me/looks/${l.id}`, { published: !l.published });
      load();
    } catch { toast.error("Could not update"); }
  };
  const remove = async (l) => {
    if (!window.confirm(`Delete "${l.title}"?`)) return;
    try {
      await api.delete(`/stylists/me/looks/${l.id}`);
      toast.success("Look removed.");
      load();
    } catch { toast.error("Could not delete"); }
  };

  return (
    <section data-testid="studio-portfolio-tab" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="eyebrow">Portfolio</p>
          <p className="text-xs text-muted-foreground mt-1">Publish looks that show up on your public profile.</p>
        </div>
        <Button
          data-testid="studio-portfolio-add-btn"
          onClick={() => setShowForm((v) => !v)}
          className="rounded-none h-10 uppercase tracking-[0.2em] text-[10px] gap-2"
        >
          <Plus size={14} /> {showForm ? "Cancel" : "Add look"}
        </Button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="border border-border p-5 space-y-4" data-testid="studio-look-form">
          <label className="block cursor-pointer border border-dashed border-border aspect-[4/5] max-w-xs bg-muted overflow-hidden relative">
            {preview ? (
              <img src={preview} alt="preview" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-muted-foreground">
                <ImageIcon size={28} strokeWidth={1.2} />
                <p className="text-xs">Click to upload · portrait 4:5 · max 10 MB</p>
              </div>
            )}
            <input
              type="file" accept="image/*" className="hidden"
              data-testid="studio-look-image-input"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                setFile(f);
                setPreview(URL.createObjectURL(f));
              }}
            />
          </label>
          <Input data-testid="studio-look-title" placeholder="Look title (e.g., Ivory Silk Sherwani for Reception)" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} maxLength={120} className="rounded-none h-11" required />
          <Textarea data-testid="studio-look-description" placeholder="Optional notes — occasion, styling story, or attribution." value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} maxLength={600} className="rounded-none" rows={3} />
          <div className="grid md:grid-cols-2 gap-3">
            <Input data-testid="studio-look-price" type="number" min={0} placeholder="Price (₹) — optional" value={form.price_inr} onChange={(e) => setForm((f) => ({ ...f, price_inr: e.target.value }))} className="rounded-none h-11" />
            <Input data-testid="studio-look-tags" placeholder="Tags (comma separated)" value={form.tags} onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))} className="rounded-none h-11" />
          </div>
          <Input data-testid="studio-look-buy" type="url" placeholder="Buy link (Amazon/Myntra/your site — optional)" value={form.buy_url} onChange={(e) => setForm((f) => ({ ...f, buy_url: e.target.value }))} className="rounded-none h-11" />
          <label className="text-xs flex items-center gap-2">
            <input type="checkbox" checked={form.published} onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))} />
            Publish immediately (uncheck to save as draft)
          </label>
          <Button type="submit" disabled={saving} data-testid="studio-look-submit" className="rounded-none h-11 uppercase tracking-[0.2em] text-[10px] gap-2">
            {saving && <Loader2 size={12} className="animate-spin" />} Publish look
          </Button>
        </form>
      )}

      {loading ? (
        <div className="py-8 flex justify-center"><Loader2 className="animate-spin" /></div>
      ) : looks.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-6" data-testid="studio-portfolio-empty">No looks yet. Add your first one above.</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="studio-looks-grid">
          {looks.map((l) => (
            <div key={l.id} className="border border-border" data-testid={`studio-look-${l.id}`}>
              <div className="aspect-[4/5] bg-muted overflow-hidden relative">
                <img src={fileUrl(l.image_path)} alt={l.title} className="w-full h-full object-cover" />
                {!l.published && <span className="absolute top-2 left-2 text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 bg-background/80 border border-border">Draft</span>}
              </div>
              <div className="p-3">
                <p className="text-sm font-serif line-clamp-1">{l.title}</p>
                {l.price_inr > 0 && <p className="text-xs text-muted-foreground mt-1">₹{l.price_inr.toLocaleString("en-IN")}</p>}
                <div className="flex items-center gap-2 mt-2">
                  <button data-testid={`studio-look-toggle-${l.id}`} onClick={() => toggle(l)} className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
                    {l.published ? <><EyeOff size={11} /> Unpublish</> : <><Eye size={11} /> Publish</>}
                  </button>
                  <button data-testid={`studio-look-delete-${l.id}`} onClick={() => remove(l)} className="ml-auto text-destructive hover:text-destructive/80" aria-label="Delete">
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export function ServicesTab() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ kind: "video", title: "Video consultation", description: "", duration_min: 45, price_inr: 999 });
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/stylists/me/services")
      .then(({ data }) => setServices(data.items || []))
      .catch(() => setServices([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/stylists/me/services", {
        ...form,
        duration_min: Number(form.duration_min) || 45,
        price_inr: Number(form.price_inr) || 0,
      });
      toast.success("Service added.");
      setForm({ kind: "video", title: "Video consultation", description: "", duration_min: 45, price_inr: 999 });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add");
    } finally { setSaving(false); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Remove "${s.title}"?`)) return;
    try {
      await api.delete(`/stylists/me/services/${s.id}`);
      load();
    } catch { toast.error("Could not remove"); }
  };
  const toggleActive = async (s) => {
    try {
      await api.patch(`/stylists/me/services/${s.id}`, { active: !s.active });
      load();
    } catch { toast.error("Could not update"); }
  };

  return (
    <section data-testid="studio-services-tab" className="space-y-6">
      <div>
        <p className="eyebrow">Services</p>
        <p className="text-xs text-muted-foreground mt-1">Set what you offer, how long it takes, and your rate.</p>
      </div>
      <form onSubmit={submit} className="border border-border p-4 grid md:grid-cols-6 gap-3 items-end" data-testid="studio-service-form">
        <label className="md:col-span-2">
          <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-1">Kind</p>
          <select data-testid="studio-service-kind" value={form.kind} onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value, title: SERVICE_KIND_META[e.target.value]?.label || f.title }))} className="w-full h-10 border border-input bg-background px-3 text-sm">
            {Object.entries(SERVICE_KIND_META).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
        </label>
        <Input data-testid="studio-service-title" placeholder="Title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="rounded-none h-10 md:col-span-2" required />
        <Input data-testid="studio-service-duration" type="number" min={15} max={240} placeholder="Duration (min)" value={form.duration_min} onChange={(e) => setForm((f) => ({ ...f, duration_min: e.target.value }))} className="rounded-none h-10" />
        <Input data-testid="studio-service-price" type="number" min={0} placeholder="Price (₹)" value={form.price_inr} onChange={(e) => setForm((f) => ({ ...f, price_inr: e.target.value }))} className="rounded-none h-10" />
        <Button type="submit" disabled={saving} data-testid="studio-service-submit" className="rounded-none h-10 uppercase tracking-[0.2em] text-[10px] gap-2 md:col-span-6">
          {saving && <Loader2 size={12} className="animate-spin" />} Add service
        </Button>
      </form>

      {loading ? (
        <div className="py-8 flex justify-center"><Loader2 className="animate-spin" /></div>
      ) : services.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-6">No services yet. Add one above so users can book you.</p>
      ) : (
        <div className="space-y-2" data-testid="studio-services-list">
          {services.map((s) => {
            const Meta = SERVICE_KIND_META[s.kind] || {};
            const Icon = Meta.icon || User;
            return (
              <div key={s.id} className="border border-border p-3 flex items-center gap-3" data-testid={`studio-service-${s.id}`}>
                <div className="h-10 w-10 border border-border flex items-center justify-center shrink-0"><Icon size={16} /></div>
                <div className="flex-1 min-w-0">
                  <p className="font-serif text-sm">{s.title}</p>
                  <p className="text-xs text-muted-foreground">{s.duration_min} min · ₹{s.price_inr.toLocaleString("en-IN")} {!s.active && "· inactive"}</p>
                </div>
                <button data-testid={`studio-service-toggle-${s.id}`} onClick={() => toggleActive(s)} className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground">
                  {s.active ? "Deactivate" : "Activate"}
                </button>
                <button data-testid={`studio-service-delete-${s.id}`} onClick={() => remove(s)} className="text-destructive hover:text-destructive/80" aria-label="Delete">
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

export function BookingsInboxTab() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/stylists/me/bookings")
      .then(({ data }) => setBookings(data.items || []))
      .catch(() => setBookings([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const act = async (b, status) => {
    setBusyId(b.id);
    try {
      const note = status === "rejected" ? window.prompt("Add a short note to the user (optional):") : null;
      await api.patch(`/stylists/me/bookings/${b.id}`, { status, note: note || undefined });
      toast.success(`Booking ${status}.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
    finally { setBusyId(null); }
  };

  return (
    <section data-testid="studio-bookings-tab" className="space-y-6">
      <div>
        <p className="eyebrow">Bookings</p>
        <p className="text-xs text-muted-foreground mt-1">Accept or decline consultation requests from clients.</p>
      </div>
      {loading ? (
        <div className="py-8 flex justify-center"><Loader2 className="animate-spin" /></div>
      ) : bookings.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-6">No booking requests yet.</p>
      ) : (
        <div className="space-y-3">
          {bookings.map((b) => (
            <div key={b.id} className="border border-border p-4" data-testid={`studio-booking-${b.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <p className="font-serif text-lg">{b.service_title}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {b.buyer?.name || b.buyer?.email || "Client"} · {b.duration_min} min · ₹{b.price_inr.toLocaleString("en-IN")}
                  </p>
                  {b.preferred_at && <p className="text-xs mt-1"><Clock size={11} className="inline mr-1" /> Preferred: {b.preferred_at}</p>}
                  {b.notes && <p className="text-sm mt-2 text-muted-foreground line-clamp-3">{b.notes}</p>}
                </div>
                <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 border ${
                  b.status === "pending" ? "border-amber-500 text-amber-600"
                    : b.status === "accepted" ? "border-accent text-accent"
                    : b.status === "cancelled" ? "border-destructive text-destructive"
                    : "border-border text-muted-foreground"
                }`}>{b.status}</span>
              </div>
              {b.status === "pending" && (
                <div className="mt-4 flex gap-2">
                  <Button data-testid={`studio-booking-accept-${b.id}`} disabled={busyId === b.id} onClick={() => act(b, "accepted")} className="rounded-none h-9 uppercase tracking-[0.18em] text-[10px] gap-2">
                    <Check size={12} /> Accept
                  </Button>
                  <Button data-testid={`studio-booking-reject-${b.id}`} disabled={busyId === b.id} onClick={() => act(b, "rejected")} variant="outline" className="rounded-none h-9 uppercase tracking-[0.18em] text-[10px] gap-2">
                    <X size={12} /> Reject
                  </Button>
                </div>
              )}
              {b.status === "accepted" && (
                <div className="mt-4 space-y-2">
                  <VideoRoomLauncher booking={b} data-testid={`studio-booking-room-${b.id}`} />
                  <Button
                    data-testid={`studio-booking-complete-${b.id}`}
                    disabled={busyId === b.id}
                    onClick={() => act(b, "completed")}
                    variant="outline"
                    className="rounded-none h-9 uppercase tracking-[0.18em] text-[10px]"
                  >
                    Mark completed
                  </Button>
                </div>
              )}
              {b.status === "completed" && (
                <PostConsultReport booking={b} onSubmitted={load} />
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * Deterministic Jitsi Meet launcher — either mints a fresh room (first
 * click) or pulls the persisted room URL on subsequent taps. Opens the
 * call in a new tab so the stylist keeps the Studio open in the
 * background.
 */
function VideoRoomLauncher({ booking }) {
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState(booking.room_name
    ? `https://meet.jit.si/${booking.room_name}`
    : null);

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await (url
        ? api.get(`/bookings/${booking.id}/room`)
        : api.post(`/bookings/${booking.id}/room`));
      setUrl(data.join_url);
      window.open(data.join_url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start the room.");
    } finally { setBusy(false); }
  };

  return (
    <Button
      onClick={start}
      disabled={busy}
      data-testid={`studio-booking-room-${booking.id}`}
      className="rounded-none h-9 uppercase tracking-[0.18em] text-[10px] gap-2"
    >
      {busy ? <Loader2 size={12} className="animate-spin" /> : <Video size={12} />}
      {url ? "Rejoin call" : "Start video call"}
    </Button>
  );
}

/**
 * Post-consultation report form. Shown on completed bookings — the
 * stylist writes a summary + key takeaways + next steps. Saving flips
 * `booking.report` and the note appears on the client's Style Journey.
 */
function PostConsultReport({ booking, onSubmitted }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(booking.report?.summary || "");
  const [takeaways, setTakeaways] = useState((booking.report?.key_takeaways || []).join("\n"));
  const [nextSteps, setNextSteps] = useState(booking.report?.next_steps || "");

  const submit = async () => {
    if (summary.trim().length < 20) {
      toast.error("Give it at least 20 characters of substance.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/bookings/${booking.id}/report`, {
        summary: summary.trim(),
        key_takeaways: takeaways.split("\n").map(t => t.trim()).filter(Boolean),
        next_steps: nextSteps.trim() || undefined,
      });
      toast.success("Report shared with the client.");
      setOpen(false);
      onSubmitted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the report.");
    } finally { setBusy(false); }
  };

  const hasReport = !!booking.report;

  if (!open) {
    return (
      <div className="mt-4 flex flex-wrap gap-2 items-center">
        {hasReport && (
          <p className="text-[11px] text-accent flex items-center gap-1">
            <Check size={12} /> Report shared on {new Date(booking.report.submitted_at).toLocaleDateString()}
          </p>
        )}
        <Button
          onClick={() => setOpen(true)}
          data-testid={`studio-booking-report-open-${booking.id}`}
          variant="outline"
          className="rounded-none h-9 uppercase tracking-[0.18em] text-[10px] gap-2"
        >
          <FileText size={12} /> {hasReport ? "Edit report" : "Write consult report"}
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-4 border border-border p-4 bg-muted/20 space-y-3" data-testid={`studio-booking-report-form-${booking.id}`}>
      <div>
        <label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Summary (min 20 chars)</label>
        <Textarea
          data-testid={`studio-report-summary-${booking.id}`}
          rows={4}
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          placeholder="What did you cover? What's their vibe now?"
          maxLength={4000}
          className="rounded-none mt-1"
        />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Key takeaways (one per line, optional)</label>
        <Textarea
          rows={3}
          value={takeaways}
          onChange={(e) => setTakeaways(e.target.value)}
          placeholder="Add pearl earrings\nAvoid warm reds\nTry the wrap dress"
          className="rounded-none mt-1"
        />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Next steps (optional)</label>
        <Input
          value={nextSteps}
          onChange={(e) => setNextSteps(e.target.value)}
          placeholder="Book a follow-up in 6 weeks"
          className="rounded-none mt-1 h-11"
        />
      </div>
      <div className="flex gap-2">
        <Button
          onClick={submit}
          disabled={busy}
          data-testid={`studio-report-submit-${booking.id}`}
          className="rounded-none h-10 uppercase tracking-[0.18em] text-[10px] gap-2"
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
          Share with client
        </Button>
        <Button
          onClick={() => setOpen(false)}
          variant="outline"
          className="rounded-none h-10 uppercase tracking-[0.18em] text-[10px]"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}

export { SERVICE_KIND_META };
